/**
 * EditDropModal Component - Form for editing an existing drop
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Alert,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { getOrders } from "../../orders/services/orderService";
import { updateDrop, getDropsByTrip, addOrderToDrop, removeOrderFromDrop } from "../services/dropService";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// Helper function to extract province from order location
const getProvince = (location) => {
  // Try addressComponents first (structured data)
  if (location?.addressComponents?.province) {
    return location.addressComponents.province;
  }
  
  // Fallback: try to parse from address string
  if (location?.address) {
    const addressParts = location.address.split(",").map(p => p.trim());
    // Province is usually last or second-to-last part
    const possibleProvince = addressParts[addressParts.length - 1] || 
                           addressParts[addressParts.length - 2];
    return possibleProvince || "N/A";
  }
  
  return "N/A";
};

// Format order display: Order ID - Customer Name - Pickup Province → Delivery Province
const formatOrderDisplay = (order) => {
  const customer = order.customer || "N/A";
  const pickupProvince = getProvince(order.pickup);
  const deliveryProvince = getProvince(order.delivery);
  return `${order.id} - ${customer} - ${pickupProvince} → ${deliveryProvince}`;
};

const PROVINCES = [
  "Western Cape",
  "Eastern Cape",
  "KwaZulu-Natal",
  "Gauteng",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
  "Free State",
];

const EditDropModal = ({ open, drop, tripId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [currentOrderIds, setCurrentOrderIds] = useState([]);

  const fetchOrders = useCallback(async (currentDropId) => {
    if (!tripId) return;
    
    try {
      setLoadingOrders(true);
      
      // Fetch all orders for this trip
      const ordersResponse = await getOrders({ tripId });
      const allTripOrders = ordersResponse.success && ordersResponse.data 
        ? ordersResponse.data 
        : [];

      // Fetch existing drops for this trip to see which orders are assigned
      let existingDrops = [];
      try {
        existingDrops = await getDropsByTrip(tripId);
      } catch (dropError) {
        logError("Failed to fetch existing drops", { 
          tripId,
          error: dropError.message 
        });
      }

      // Extract order IDs that are already assigned to OTHER drops (not this one)
      const assignedOrderIds = new Set();
      existingDrops.forEach(existingDrop => {
        // Skip the current drop we're editing
        if (existingDrop.id === currentDropId) {
          return;
        }
        
        if (existingDrop.orders && existingDrop.orders.length > 0) {
          existingDrop.orders.forEach(order => {
            const orderId = typeof order === 'string' ? order : order.orderId;
            if (orderId) {
              assignedOrderIds.add(orderId);
            }
          });
        }
      });

      // Show all trip orders, but mark assigned ones differently if needed
      setOrders(allTripOrders);
      
      logInfo("EditDropModal: Fetched orders", {
        tripId,
        dropId: currentDropId,
        totalOrders: allTripOrders.length,
        assignedToOtherDrops: assignedOrderIds.size,
      });
    } catch (err) {
      logError("Failed to fetch orders", { 
        tripId,
        error: err.message 
      });
      setError(err.message || "Failed to fetch orders");
    } finally {
      setLoadingOrders(false);
    }
  }, [tripId]);

  useEffect(() => {
    if (open && drop && tripId) {
      fetchOrders(drop.id);
      form.resetFields();
      setError(null);
      
      // Extract current order IDs from drop
      const currentOrders = drop.orders?.map(order => 
        typeof order === 'string' ? order : order?.orderId
      ).filter(Boolean) || [];
      
      setCurrentOrderIds(currentOrders);
      setSelectedOrderIds(currentOrders);
      
      // Pre-populate form with drop data
      form.setFieldsValue({
        city: drop.location?.city || "",
        province: drop.location?.province || "",
        address: drop.location?.address || "",
        sequence: drop.sequence || 1,
        status: drop.status || "pending",
        orderIds: currentOrders,
      });
    }
  }, [open, drop, tripId, form, fetchOrders]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);

      // Update basic drop fields (location, sequence, status)
      const updateData = {
        location: {
          city: values.city,
          province: values.province,
          address: values.address || "",
          // Preserve coordinates if they exist
          coordinates: drop.location?.coordinates || undefined,
        },
        sequence: values.sequence,
        status: values.status,
      };

      // Update drop basic fields first
      const updateResult = await updateDrop(drop.id, updateData);
      if (!updateResult.success) {
        throw new Error(updateResult.error || "Failed to update drop");
      }

      // Handle order changes: compare current vs selected
      const selectedOrders = values.orderIds || [];
      const ordersToAdd = selectedOrders.filter(id => !currentOrderIds.includes(id));
      const ordersToRemove = currentOrderIds.filter(id => !selectedOrders.includes(id));

      // Add new orders
      for (const orderId of ordersToAdd) {
        try {
          await addOrderToDrop(drop.id, orderId);
          logInfo("Order added to drop", { dropId: drop.id, orderId });
        } catch (addError) {
          logError("Failed to add order to drop", {
            dropId: drop.id,
            orderId,
            error: addError.message,
          });
          // Continue with other orders even if one fails
        }
      }

      // Remove orders
      for (const orderId of ordersToRemove) {
        try {
          await removeOrderFromDrop(drop.id, orderId);
          logInfo("Order removed from drop", { dropId: drop.id, orderId });
        } catch (removeError) {
          logError("Failed to remove order from drop", {
            dropId: drop.id,
            orderId,
            error: removeError.message,
          });
          // Continue with other orders even if one fails
        }
      }

      logInfo("Drop updated successfully", { dropId: drop.id });
      
      if (onSuccess) {
        onSuccess(updateResult.data);
      }
    } catch (err) {
      logError("Failed to update drop", { error: err.message });
      setError(err.message || "Failed to update drop");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <EditOutlined style={{ color: "#1890ff" }} />
          <Text strong>Edit Drop</Text>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnHidden
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          city: "",
          province: "",
          address: "",
          sequence: 1,
          status: "pending",
          orderIds: [],
        }}
      >
        <Form.Item
          name="city"
          label="City"
          rules={[
            {
              required: true,
              message: "Please enter the city",
            },
          ]}
        >
          <Input placeholder="Enter city name" />
        </Form.Item>

        <Form.Item
          name="province"
          label="Province"
          rules={[
            {
              required: true,
              message: "Please select a province",
            },
          ]}
        >
          <Select placeholder="Select province" showSearch>
            {PROVINCES.map((province) => (
              <Option key={province} value={province}>
                {province}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="address" label="Address (Optional)">
          <TextArea
            rows={2}
            placeholder="Enter full address..."
          />
        </Form.Item>

        <Form.Item
          name="sequence"
          label="Sequence Number"
          rules={[
            {
              required: true,
              message: "Please enter sequence number",
            },
            {
              type: "number",
              min: 1,
              message: "Sequence must be at least 1",
            },
          ]}
        >
          <InputNumber
            min={1}
            style={{ width: "100%" }}
            placeholder="Enter sequence (1, 2, 3...)"
          />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[
            {
              required: true,
              message: "Please select a status",
            },
          ]}
        >
          <Select placeholder="Select status">
            <Option value="pending">Pending</Option>
            <Option value="arrived">Arrived</Option>
            <Option value="completed">Completed</Option>
          </Select>
        </Form.Item>

        <Form.Item 
          name="orderIds" 
          label="Orders (Optional)"
          getValueFromEvent={(value) => {
            // Ensure we always return an array
            if (!value) return [];
            const result = Array.isArray(value) ? value : [value];
            setSelectedOrderIds(result);
            return result;
          }}
        >
          <Select
            mode="multiple"
            placeholder="Select orders for this drop"
            loading={loadingOrders}
            optionFilterProp="label"
            showSearch
            onChange={(value) => {
              setSelectedOrderIds(value || []);
              form.setFieldsValue({ orderIds: value || [] });
            }}
          >
            {orders.map((order) => {
              const label = formatOrderDisplay(order);
              return (
                <Option key={order.id} value={order.id} label={label}>
                  {label}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
        
        <Form.Item label="" colon={false}>
          <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: -16 }}>
            {selectedOrderIds.length > 0
              ? `${selectedOrderIds.length} order${selectedOrderIds.length > 1 ? 's' : ''} selected`
              : "Select orders to be delivered at this drop location"}
          </Text>
        </Form.Item>

        <Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update Drop
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditDropModal;

